import { createContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { IntlProvider as UseIntlProvider } from "use-intl";
import {
  DEFAULT_LOCALE,
  getLocaleFromCookie,
  writeLocaleCookie,
  type Locale,
} from "./locale-cookie";
import { loadMessages } from "./load-messages";

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
}

export const LocaleContext = createContext<LocaleContextValue | null>(null);

interface AppIntlProviderProps {
  children: ReactNode;
  timeZone?: string;
}

export function AppIntlProvider({ children, timeZone = "Asia/Ho_Chi_Minh" }: AppIntlProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => getLocaleFromCookie());
  const [messages, setMessages] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMessages(locale).then((m) => {
      if (!cancelled) setMessages(m);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const ctx = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: (next) => {
        writeLocaleCookie(next);
        setLocaleState(next);
      },
    }),
    [locale],
  );

  if (!messages) {
    // Avoid flashing keys on first paint while messages load.
    return null;
  }

  return (
    <LocaleContext.Provider value={ctx}>
      <UseIntlProvider
        locale={locale}
        messages={messages}
        timeZone={timeZone}
        onError={(error) => {
          if (import.meta.env.DEV) {
            console.warn("[i18n]", error.code, error.message);
          }
        }}
        getMessageFallback={({ key, namespace }) =>
          namespace ? `${namespace}.${key}` : key
        }
      >
        {children}
      </UseIntlProvider>
    </LocaleContext.Provider>
  );
}

export { DEFAULT_LOCALE, type Locale };
